import {Component, Input, NgZone, ChangeDetectorRef} from "@angular/core";

import {ModalConfig} from "../services/notification/modal.config";
import {EventBus} from "../event/EventBus";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";

import {ComponentBase} from "../base/ComponentBase";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {PipelineService, IGlossaryInfo} from "../services/pipeline/pipeline.service";

import {ISelection, IGlossaryEntry, ISyllables, IDefinition,
    IPartOfSpeech, IFact, IContext, ITranslation, ICredit} from  "../api/data-model";
import {Subscription} from "rxjs";
import {ContentCursor} from "../services/content/content.cursor";
import {CssWord} from "../content/content-view.objects";
import {PipelineSaveEvent, PIPELINE_API_STATUS} from "./PipelineSaveEvent";
import {RaVersion} from "../services/configuration/ra.version";
import {UtilitiesService} from "../services/utilities.service";
import {HttpClient} from "@angular/common/http";
import {AudioState} from "../constants";

@Component({
    selector: "glossary-editor",
    templateUrl: "./glossary.editor.component.html",
    styleUrls: ["./pipeline.component.scss"]
})
export class GlossaryEditorComponent extends ComponentBase {

    public activateModal: boolean = false;
    public status:string = "";
    public saveInProgress:boolean = false;
    public activateEntryVerificationModal: boolean = false;
    public saveCapitalizeEntry: boolean = false;
    public glossaryInfoToSave: IGlossaryInfo = null;

    public showAdd:boolean = false;
    public glossarySearchResult:IGlossaryInfo[] = null;
    public newGlossaryInfo:IGlossaryInfo = {isDefinition: false,
        entry: GlossaryEditorComponent.buildNewGlossaryEntry()};
    public selectedWord:CssWord = null;
    public saveSelectedWord:CssWord = null;
    public searchTerm:string = "";
    public lastSearchTerm:string = "";
    public linkTerm:string = "";
    public searching:boolean = false;
    private scSubscription:Subscription;

    constructor (
        private http:HttpClient,
        private eventBus: EventBus,
        private modalConfig: ModalConfig,
        private logService: BugsnagLoggingService,
        private _ngZone: NgZone,
        private pipelineService:PipelineService,
        private contentCursor:ContentCursor,
        private ref: ChangeDetectorRef
    ) {
        super("GlossaryEditorComponent");
    }

    init(): void {
        this.scSubscription = this.contentCursor.wordChanged.subscribe(word => {
            this.logService.log("WORD CLICKED: " + word.word + " glossary " + word.glossary +
                                " window selection: "  + window.getSelection());
            this.selectedWord = word;
            this.searchTerm = this.pipelineService.getGlossaryTerm(word, word.parentSentence());
            this.logService.log("returned search term:" + this.searchTerm + ":");
            this.searchGlossary();
        });
        this.subscriptions.push(this.scSubscription);
    }

    cleanup(): void {
        if ( this.scSubscription) {
            this.scSubscription.unsubscribe();
        }
    }

    public searchGlossary():void {
        this.searching = true;
        this.glossarySearchResult = [];
        if (this.searchTerm !== this.lastSearchTerm) {
            this.showAdd = false;
            this.newGlossaryInfo = {isDefinition: false, entry: GlossaryEditorComponent.buildNewGlossaryEntry()};
        }
        this.pipelineService.searchGlossary(this.searchTerm).subscribe( result => {
            this.prepareGlossaryUIData(result);
        }, err => {
            this.logService.log("error on search glossary");
            this.lastSearchTerm = this.searchTerm;
            this.searching = false;
            this.linkTerm = "";
        });
    }

    private prepareGlossaryUIData(result): void {
        let glossaryWords = this.searchTerm.trim().split(" ");
        this.linkTerm = (glossaryWords.length > 1) ? this.getLinkTerm(glossaryWords.length) : "";
        this.lastSearchTerm = this.searchTerm;
        this.searching = false;
        result.forEach((glossaryEntry) => {
            this.glossarySearchResult.push({
                isDefinition: this.isTheDefinition(glossaryEntry),
                entry: glossaryEntry
            });
            this.logService.log("isDefinition=" + this.isTheDefinition(glossaryEntry) +
            "for term " + this.searchTerm);
        });
        this.glossarySearchResult.sort((a, b) => Number(b.isDefinition) - Number(a.isDefinition));
    }

    private getLinkTerm(numWords: number): string {
        let linkTerm = "";
        let currentWord = this.selectedWord;
        for (let i = 0; (i < numWords) && currentWord; i++) {
            linkTerm += currentWord.word + " ";
            currentWord = currentWord.parentSentence().nextChild(currentWord);
        }
        return linkTerm.trim();

    }

    public isTheDefinition(glossaryEntry: IGlossaryEntry):boolean {
        // let glossaryEntryItem = this.glossaryEntries.filter((entry)=>entry.id === id);
        return (!this.selectedWord ? false : (this.selectedWord.glossary === glossaryEntry.id));
    }

    public showAddGlossary():void {
        this.showAdd = true;
        this.ref.detectChanges();
    }

    public clearGlossary():void {
        this.glossarySearchResult = null;
        this.searchTerm = "";
        this.linkTerm = "";
    }

    public showSelected():void {
        this.logService.log("setting to start at this word via user with ngzone: " + this.selectedWord.word);
        this._ngZone.run(() => {
            this.contentCursor.userSelectWord(this.selectedWord);
            this.ref.detectChanges();
        });

    }

    public saveLinkGlossaryEntry(glossaryInfo: IGlossaryInfo): void {
        this.saveCapitalizeEntry = false;
        if (this.isWordAtBeginningAndCapitalize()) {
            this.activateEntryVerificationModal = true;
            this.glossaryInfoToSave = glossaryInfo;
        } else {
            this.saveGlossaryEntry(glossaryInfo, true);
        }
    }

    public updateGlossaryEntry(glossaryInfo: IGlossaryInfo): void {
        console.log("updating entry:" + glossaryInfo.entry.word);
        this.saveGlossaryEntry(glossaryInfo, false);
    }

    public saveGlossaryEntry(glossaryInfo: IGlossaryInfo, doLink:boolean): void {

        /*
         If this is a new glossary entry, assign it an id based on
        the size of the existing glossary list: name + "-" + size + 1
         */
        this.saveSelectedWord = this.selectedWord;
        let glossaryEntry:IGlossaryEntry = glossaryInfo.entry;
        if (!glossaryEntry.id) { // new glossary term
            this.addNewId(glossaryEntry);
            this.addAudioState(glossaryEntry);
        }
        console.log(JSON.stringify(glossaryEntry));
        // TODO: if translation exists and no id, add that here too.  addNewTransId.  Audio state?  need a placeholder?
        if (glossaryEntry.translations && glossaryEntry.translations.length > 0 && !glossaryEntry.translations[0].id) {
            glossaryEntry.translations[0].id = UtilitiesService.getTranslationId(glossaryEntry.id, glossaryEntry.translations[0].language);
        }
        this.doSmartQuotes(glossaryEntry);
        if(!this.saveCapitalizeEntry){
            glossaryEntry.word = glossaryEntry.word.toLowerCase();
        }
        this.saveInProgress = true;
        this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.start));
        this.pipelineService.saveGlossary(glossaryEntry).subscribe(result => {
            if (!this.glossarySearchResult) {
                this.glossarySearchResult = [];
            }
            if (this.saveSelectedWord === this.selectedWord) {
                let resultWithId = this.glossarySearchResult.filter((g) => {
                    return g.entry.id === glossaryInfo.entry.id;
                });
                if (resultWithId.length === 0) {
                    this.glossarySearchResult.push(glossaryInfo);
                }
            }
            this.saveInProgress = false;
            this.newGlossaryInfo = {isDefinition: false, entry: GlossaryEditorComponent.buildNewGlossaryEntry()};
            this.showAdd = false;
            this.logService.log("saved glossary entry: " + JSON.stringify(result));
            this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.finish,
                this.pipelineService.currentSourceSelection));
            if (doLink) {
                this.makeDefinition(glossaryInfo);
            } else {
                this.saveSelectedWord = null;
            }
        });
    }

    private addNewId(glossaryEntry: IGlossaryEntry) {
        if (!glossaryEntry.word) {
            glossaryEntry.word = this.searchTerm;
        }
        let newGlossaryIdBase: string = UtilitiesService.convertWordOrPhraseToID(glossaryEntry.word);

        let newGlossaryId = newGlossaryIdBase + (!this.glossarySearchResult ||
        this.glossarySearchResult.length === 0 ? 1 :
            this.glossarySearchResult.length + 1);
        this.logService.log("adding id " + newGlossaryId + " based on list length "
            + (!this.glossarySearchResult ? 0 : this.glossarySearchResult.length));
        glossaryEntry.id = newGlossaryId;
    }

    private addAudioState(glossaryEntry: IGlossaryEntry) {
        glossaryEntry.audioState = AudioState.UNRECORDED;
        glossaryEntry.syllables.audioState = AudioState.UNRECORDED;
        glossaryEntry.definition.audioState = AudioState.UNRECORDED;
        glossaryEntry.context.audioState = AudioState.UNRECORDED;
        if (glossaryEntry.fact) {
            glossaryEntry.fact.audioState = AudioState.UNRECORDED;
        }
        glossaryEntry.translations = glossaryEntry.translations.map(trans => ({...trans, audioState: AudioState.UNRECORDED}));
    }

    private doSmartQuotes(glossaryEntry: IGlossaryEntry): void {
        glossaryEntry.definition.text = UtilitiesService.smarten(glossaryEntry.definition.text);
        glossaryEntry.context.text = UtilitiesService.smarten(glossaryEntry.context.text);
        glossaryEntry.fact.text = UtilitiesService.smarten(glossaryEntry.fact.text);
        glossaryEntry.translations.forEach((trans: ITranslation) => {
            trans.word = UtilitiesService.smarten(trans.word);
        });
    }

    public deleteGlossaryEntry(glossaryInfo: IGlossaryInfo): void {
        if (confirm("Are you sure you want to delete this?") === true) {
            this.saveInProgress = true;
            this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.start));
            this.pipelineService.deleteGlossary(glossaryInfo.entry).subscribe(result => {
                this.saveInProgress = false;
                this.logService.log("Deleted glossary entry: " + result);
                this.cancelGlossaryEntry(glossaryInfo);
                this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.finish,
                    this.pipelineService.currentSourceSelection));
            });

        }
    }

    public cancelGlossaryEntry(glossaryInfo: IGlossaryInfo): void {
        this.logService.log("Removing entry from UI");
        this.showAdd = false;
        this.newGlossaryInfo = {isDefinition: false, entry: GlossaryEditorComponent.buildNewGlossaryEntry()};
        let index = this.glossarySearchResult.indexOf(glossaryInfo, 0);
        if (index > -1) {
            this.glossarySearchResult.splice(index, 1);
        }
        if (this.glossarySearchResult.length === 0) {
            this.searchGlossary();
        }
    }

    public makeDefinition(glossaryInfo: IGlossaryInfo): void {
        this.logService.log("make definition..." +
                            (glossaryInfo.isDefinition ? "link" : "unlink") + " id=" + glossaryInfo.entry.id);
        let word = this.saveSelectedWord ? this.saveSelectedWord : this.selectedWord;
        let text =
            this.pipelineService.tagGlossary(word, glossaryInfo, this.linkTerm,
                                             this.pipelineService.currentSourceSelection.text);
        if (text) {
            this.pipelineService.currentSourceSelection.text = text;
            this.saveSelection(glossaryInfo.entry);
        } else {
            alert("WARNING! no matching text found for: " + glossaryInfo.entry.word + ": nothing linked or unlinked");
        }
    }

    public saveSelection(entry: IGlossaryEntry): void {
        this.saveInProgress = true;
        this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.start));
        this.pipelineService.saveSourceContent().subscribe(result => {
            this.logService.log("saved uninflated, done result.ok=" + result?.ok);
            this.eventBus.next(new PipelineSaveEvent(PIPELINE_API_STATUS.finish,
                this.pipelineService.currentSourceSelection));
            this.clearGlossary();
            this.ref.detectChanges();
            this.saveInProgress = false;
            this.selectedWord = null;
            this.saveSelectedWord = null;
        }, (error) => {
            alert("ERROR: selection save (link/unlink) failed! " + error.status);
        });
    }

    public static buildNewGlossaryEntry():GlossaryEntry {
        let glossaryEntry:GlossaryEntry = new GlossaryEntry();
        glossaryEntry.context = {text:"", audio:"", media:""};
        glossaryEntry.definition = {text:"", audio:"", media:""};
        glossaryEntry.partOfSpeech = {text:"", audio:"", media:""};
        glossaryEntry.syllables = {text:"", audio:"", media:""};
        glossaryEntry.fact = {text:"", audio:"", media:""};
        glossaryEntry.translations = [];
        glossaryEntry.imageCredits = [];
        return glossaryEntry;
    }

    public makeActive():void {
        this.init();
    }

    public makeInactive():void {
        this.cleanup();
    }

    public saveVerifiedGlossaryEntry(): void {
        this.activateEntryVerificationModal = false;
        this.saveGlossaryEntry(this.glossaryInfoToSave, true);
    }

    public isWordAtBeginningAndCapitalize(): boolean {
        const sentenceWords = this.selectedWord.parentSentence().words
        return /[A-Z]/.test(this.selectedWord.word) && sentenceWords[0].word === this.selectedWord.word;
    }

    public closeCapitalizeVerificationModal(): void {
        this.showAdd = false;
        this.activateEntryVerificationModal = false;
    }

}

export class GlossaryEntry implements IGlossaryEntry {
    word: string;
    id: string;
    key: string;
    normalAudio: string;
    syllables: ISyllables;
    definition: IDefinition;
    partOfSpeech: IPartOfSpeech;
    context: IContext;
    fact: IFact;
    translations: ITranslation[];
    language: string;
    imageCredits: ICredit[];
}
